// ProtectedRoute.js
import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import usePlanFeatures from "../hooks/usePlanFeatures";

const IS_SAAS = process.env.REACT_APP_IS_SAAS === 'true';
// Abonelik süresi dolduğunda bile erişilebilir sayfalar
const BILLING_EXEMPT_PATHS = ['/billing', '/settings'];
// Cashier rolünün erişebildiği sayfalar (diğerleri /home'a yönlendirilir)
const CASHIER_ALLOWED_PATHS = ['/home', '/appointments', '/customers', '/services'];
const PLAN_GATED_PATHS = [
  { path: '/appointments', feature: 'appointments' },
  { path: '/loyalty-config', feature: 'loyalty' },
  { path: '/business-profile', feature: 'appointments' },
  { path: '/sms-settings', feature: 'sms' },
  { path: '/packages', feature: 'packages' },
  { path: '/commissions', feature: 'commissions' },
];

const ProtectedRoute = ({ children }) => {
  const { user, loading, authInitialized } = useAuth();
  const { isFeatureAllowed } = usePlanFeatures();
  const location = useLocation();

  // Auth başlatılmamışsa hiçbir şey gösterme
  if (!authInitialized) {
    return null;
  }

  // Yükleme durumunda loading komponenti göster
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div>Yükleniyor...</div>
      </div>
    );
  }

  // Kullanıcı oturum açmamışsa login sayfasına yönlendir
  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // SaaS modda abonelik süresi dolmuşsa /billing'e yönlendir
  if (IS_SAAS && user.subscription) {
    const { status, currentPeriodEnd, manuallyManaged } = user.subscription;
    const isExpiredStatus = status === 'past_due' || status === 'suspended' || status === 'canceled';
    // Client-side expiry check: cron/token refresh beklenmeden anında yakala
    const isExpiredByDate = !manuallyManaged &&
      status === 'active' &&
      currentPeriodEnd instanceof Date &&
      currentPeriodEnd < new Date();
    if ((isExpiredStatus || isExpiredByDate) &&
        !BILLING_EXEMPT_PATHS.includes(location.pathname)) {
      return <Navigate to="/billing" replace />;
    }
  }

  // Plan bazlı rota koruması
  if (IS_SAAS && user) {
    const gated = PLAN_GATED_PATHS.find((p) => location.pathname.startsWith(p.path));
    if (gated && !isFeatureAllowed(gated.feature)) {
      return <Navigate to="/billing" replace />;
    }
  }

  // Superadmin sadece /admin sayfasını kullanır
  if (user.superadmin && !location.pathname.startsWith('/admin')) {
    return <Navigate to="/admin" replace />;
  }

  // Cashier rolü sadece izin verilen sayfalara erişebilir
  if (user.role === 'cashier') {
    const isAllowed = CASHIER_ALLOWED_PATHS.some((p) => location.pathname.startsWith(p));
    if (!isAllowed) {
      return <Navigate to="/home" replace />;
    }
  }

  return children;
};

export default ProtectedRoute;